import React, { useEffect, useRef } from 'react';

import { useUI } from '@/stores/useAppStore';
import { pinsSidebarCollapsed } from '@shared/app-identity/appChrome';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { useHasInlineSidebar } from '@shared/hooks/useHasInlineSidebar';

import SideNavHeader from './SideNavHeader';
import SideNavProfile from './SideNavProfile';
import SideNavTopControls from './SideNavTopControls';
import StarredRows from './StarredRows';
import StatusLine from './StatusLine';
import UpdateChip from './UpdateChip';
import { useNavCounts } from './useNavCounts';
import { useSidebarTransition } from './useSidebarTransition';

interface SideNavProps {
  onNavigate?: () => void;
}

const SideNav: React.FC<SideNavProps> = ({ onNavigate }) => {
  const { isSidebarCollapsed, setSidebarCollapsed } = useUI();
  const wasAutoCollapsedRef = useRef(false);
  const navCounts = useNavCounts();
  // Scoped to the running installed app: an area app renders only its own rows,
  // however few. The shell around them — the profile footer — always stays.
  const installedApp = useInstalledApp();

  // An app on the minimal shell pins the rail collapsed: its column holds one
  // nav icon, so the stored width has almost nothing to reveal. Read, never
  // written — the same account in a browser tab keeps the width it chose there.
  const pinnedCollapsed = pinsSidebarCollapsed(installedApp);
  const isCollapsed = pinnedCollapsed || isSidebarCollapsed;
  // Delayed flag: switches to collapsed layout only after CSS transition ends
  const visualCollapsed = useSidebarTransition(isCollapsed);

  // Auto-collapse the sidebar when the viewport drops below the nav breakpoint,
  // and restore it — only if we were the ones who collapsed it — when the
  // viewport comes back. Reads the shared viewport hook so this and the CSS
  // `nav:` variant can never disagree about where the line is.
  const hasInlineSidebar = useHasInlineSidebar();
  useEffect(() => {
    // A pinned rail is already collapsed and must not touch the stored
    // preference — writing it here would leak the app's fixed width back into
    // the browser tab the next time the window was resized.
    if (pinnedCollapsed) return;

    if (!hasInlineSidebar && !isSidebarCollapsed) {
      setSidebarCollapsed(true);
      wasAutoCollapsedRef.current = true;
    } else if (hasInlineSidebar && isSidebarCollapsed && wasAutoCollapsedRef.current) {
      setSidebarCollapsed(false);
      wasAutoCollapsedRef.current = false;
    }
  }, [hasInlineSidebar, pinnedCollapsed, isSidebarCollapsed, setSidebarCollapsed]);

  return (
    <div className="bg-sidebar text-sidebar-foreground flex h-full flex-col">
      {/* Pinned: the collapse toggle, then the Go box. */}
      <SideNavHeader isCollapsed={visualCollapsed} {...(onNavigate && { onNavigate })} />

      {/* One scroll column: Home / chat rows, then the starred rows and All areas.
          With many stars only this block scrolls; header and footer never move. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <SideNavTopControls isCollapsed={visualCollapsed} />

        {/* The rail is the starred set, then All areas. No groups, no accordions —
            grouping lives on the All-areas page now. */}
        <nav className="px-2 pt-1 pb-2">
          <StarredRows
            isCollapsed={visualCollapsed}
            counts={navCounts}
            {...(onNavigate && { onNavigate })}
          />
        </nav>
      </div>

      {/* Footer: the update chip (only after a deploy), the status line, then
          the avatar. Pinned below the scroll column. */}
      <div className="p-2">
        {visualCollapsed ? (
          // ml-3 centres the icon over the avatar, as the collapsed status dot does.
          <div className="ml-3">
            <UpdateChip compact />
          </div>
        ) : (
          <UpdateChip />
        )}
        <StatusLine isCollapsed={visualCollapsed} pinnedCollapsed={pinnedCollapsed} />
        <SideNavProfile isCollapsed={visualCollapsed} onNavigate={onNavigate} />
      </div>
    </div>
  );
};

export default SideNav;
